import React, { useState, useEffect, useCallback } from 'react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { barrelService } from '../../services/barrelService';
import { cartonService } from '../../services/cartonService';
import barrelReplenishmentService from '../../services/barrelReplenishmentService';
import { supabase } from '../../lib/supabase';
import { TrendingUp, RefreshCw, AlertTriangle, X, Wine, Package, CheckCircle, Plus, Trash2, Mail } from 'lucide-react';

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Approuvé', color: 'bg-blue-100 text-blue-700' },
  ordered: { label: 'Commandé', color: 'bg-purple-100 text-purple-700' },
  received: { label: 'Reçu', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700' }
};

const BARREL_TYPE_LABELS = {
  noir_270l: 'Barrique Noire 270L',
  bleu_220l: 'Barrique Bleue 220L'
};

const BARREL_OPTIONS = [
  { value: 'barrel__noir_270l', label: 'Barrique Noire 270L', type: 'barrel', barrelType: 'noir_270l' },
  { value: 'barrel__bleu_220l', label: 'Barrique Bleue 220L', type: 'barrel', barrelType: 'bleu_220l' },
];

const generateOrderRef = () => {
  const now = new Date();
  return `CMD-${now?.getFullYear()}${String(now?.getMonth() + 1)?.padStart(2, '0')}${String(now?.getDate())?.padStart(2, '0')}-${Math.floor(Math.random() * 9000 + 1000)}`;
};

const createEmptyLine = () => ({
  id: Date.now() + Math.random(),
  productKey: '',
  supplierId: '',
  quantity: 1,
  unitPrice: 0,
  lineTotal: 0
});

export default function UnifiedReplenishmentCenter() {
  const [barrelInventory, setBarrelInventory] = useState([]);
  const [cartonInventory, setCartonInventory] = useState([]);
  const [replenishmentStats, setReplenishmentStats] = useState(null);
  const [stockLevels, setStockLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Suppliers
  const [barrelSuppliers, setBarrelSuppliers] = useState([]);
  const [cartonSuppliers, setCartonSuppliers] = useState([]);

  // Multi-line order form
  const [orderLines, setOrderLines] = useState([createEmptyLine()]);
  const [orderNotes, setOrderNotes] = useState('');
  const [orderRef] = useState(generateOrderRef());

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bInv, cInv, stats, levels, bSuppliers, cSuppliers] = await Promise.all([
        barrelService?.getInventory(),
        cartonService?.getCartonInventory(),
        barrelReplenishmentService?.getReplenishmentStatistics()?.catch(() => null),
        barrelReplenishmentService?.checkStockLevels()?.catch(() => []),
        barrelReplenishmentService?.getSuppliers({ isActive: true })?.catch(() => []),
        cartonService?.getCartonSuppliers()?.catch(() => [])
      ]);
      setBarrelInventory(bInv || []);
      setCartonInventory(cInv || []);
      setReplenishmentStats(stats);
      setStockLevels(levels || []);
      setBarrelSuppliers(bSuppliers || []);
      setCartonSuppliers(cSuppliers || []);
    } catch (e) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Build product options for dropdown
  const productOptions = [
    ...BARREL_OPTIONS,
    ...cartonInventory?.map(ci => ({
      value: `carton__${ci?.id}`,
      label: ci?.cartonType,
      type: 'carton',
      cartonId: ci?.id,
      unitPriceBuy: ci?.unitPriceBuy || 0
    }))
  ];

  // Get suppliers for a given product key
  const getSuppliersForProduct = (productKey) => {
    if (!productKey) return [];
    if (productKey?.startsWith('barrel__')) return barrelSuppliers;
    if (productKey?.startsWith('carton__')) return cartonSuppliers;
    return [];
  };

  // Get unit price for a supplier + product combination
  const getUnitPriceForSupplier = (productKey, supplierId) => {
    if (!supplierId) return 0;
    if (productKey?.startsWith('barrel__')) {
      const supplier = barrelSuppliers?.find(s => String(s?.id) === String(supplierId));
      return parseFloat(supplier?.unitPrice || supplier?.unit_price || 0);
    }
    if (productKey?.startsWith('carton__')) {
      const supplier = cartonSuppliers?.find(s => String(s?.id) === String(supplierId));
      return parseFloat(supplier?.unitPrice || supplier?.unit_price || 0);
    }
    return 0;
  };

  const updateLine = (lineId, field, value) => {
    setOrderLines(prev => prev?.map(line => {
      if (line?.id !== lineId) return line;
      const updated = { ...line, [field]: value };

      // Auto-update unit price when supplier changes
      if (field === 'supplierId') {
        updated.unitPrice = getUnitPriceForSupplier(line?.productKey, value);
      }
      // Auto-update unit price when product changes (reset supplier)
      if (field === 'productKey') {
        updated.supplierId = '';
        updated.unitPrice = 0;
      }
      // Recalculate line total
      const qty = field === 'quantity' ? parseInt(value) || 0 : parseInt(updated?.quantity) || 0;
      const price = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(updated?.unitPrice) || 0;
      updated.lineTotal = qty * price;
      return updated;
    }));
  };

  const addLine = () => setOrderLines(prev => [...prev, createEmptyLine()]);
  const removeLine = (lineId) => {
    if (orderLines?.length === 1) return;
    setOrderLines(prev => prev?.filter(l => l?.id !== lineId));
  };

  const totalOrderAmount = orderLines?.reduce((sum, l) => sum + (l?.lineTotal || 0), 0);

  // Group lines by supplier for email sending
  const getLinesBySupplier = () => {
    const groups = {};
    orderLines?.forEach(line => {
      if (!line?.supplierId || !line?.productKey) return;
      const supplierId = line?.supplierId;
      if (!groups?.[supplierId]) groups[supplierId] = [];
      groups?.[supplierId]?.push(line);
    });
    return groups;
  };

  const getSupplierById = (supplierId, productKey) => {
    if (productKey?.startsWith('barrel__')) return barrelSuppliers?.find(s => String(s?.id) === String(supplierId));
    return cartonSuppliers?.find(s => String(s?.id) === String(supplierId));
  };

  const getProductLabel = (productKey) => {
    return productOptions?.find(p => p?.value === productKey)?.label || productKey;
  };

  const handleCreateOrder = async () => {
    const validLines = orderLines?.filter(l => l?.productKey && l?.quantity > 0);
    if (validLines?.length === 0) {
      setError('Veuillez ajouter au moins une ligne de commande valide');
      return;
    }
    setSaving(true);
    try {
      for (const line of validLines) {
        if (line?.productKey?.startsWith('barrel__')) {
          const barrelType = line?.productKey?.replace('barrel__', '');
          await barrelReplenishmentService?.createPurchaseOrder({
            barrel_type: barrelType,
            quantity: line?.quantity,
            unit_price: line?.unitPrice,
            total_amount: line?.lineTotal,
            supplier_id: line?.supplierId || null,
            notes: orderNotes
          });
        } else if (line?.productKey?.startsWith('carton__')) {
          const cartonId = line?.productKey?.replace('carton__', '');
          await cartonService?.adjustCartonStock(cartonId, line?.quantity, `Réapprovisionnement: ${orderNotes}`);
        }
      }
      setShowOrderModal(false);
      setOrderLines([createEmptyLine()]);
      setOrderNotes('');
      setSuccess('Commande de réapprovisionnement créée avec succès');
      fetchData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError('Erreur lors de la création : ' + (e?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    const validLines = orderLines?.filter(l => l?.productKey && l?.quantity > 0 && l?.supplierId);
    if (validLines?.length === 0) {
      setError('Veuillez sélectionner un fournisseur pour au moins une ligne');
      return;
    }

    setSendingEmail(true);
    try {
      const linesBySupplier = getLinesBySupplier();
      const supplierIds = Object.keys(linesBySupplier);

      for (const supplierId of supplierIds) {
        const lines = linesBySupplier?.[supplierId];
        const firstLine = lines?.[0];
        const supplier = getSupplierById(supplierId, firstLine?.productKey);

        if (!supplier?.email) {
          setError(`Le fournisseur "${supplier?.name}" n'a pas d'email configuré`);
          continue;
        }

        const emailLines = lines?.map(line => ({
          productLabel: getProductLabel(line?.productKey),
          quantity: line?.quantity,
          unitPrice: parseFloat(line?.unitPrice) || 0,
          lineTotal: parseFloat(line?.lineTotal) || 0
        }));

        const supplierTotal = emailLines?.reduce((sum, l) => sum + l?.lineTotal, 0);

        const { data, error: fnError } = await supabase?.functions?.invoke('send-replenishment-order-email', {
          body: {
            supplierEmail: supplier?.email,
            supplierName: supplier?.name,
            orderLines: emailLines,
            notes: orderNotes,
            orderReference: orderRef,
            totalAmount: supplierTotal
          }
        });

        if (fnError) throw fnError;
      }

      setSuccess(`Bon(s) de commande envoyé(s) par email à ${supplierIds?.length} fournisseur(s)`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError('Erreur lors de l\'envoi email : ' + (e?.message || ''));
    } finally {
      setSendingEmail(false);
    }
  };

  const openModal = (prefillLine = null) => {
    if (prefillLine) {
      setOrderLines([{ ...createEmptyLine(), ...prefillLine }]);
    } else {
      setOrderLines([createEmptyLine()]);
    }
    setOrderNotes('');
    setShowOrderModal(true);
  };

  const lowBarrels = barrelInventory?.filter(i => {
    const stock = i?.stockQuantity || i?.stock_quantity || 0;
    const threshold = i?.reorderThreshold || i?.reorder_threshold || 20;
    return stock <= threshold;
  });

  const lowCartons = cartonInventory?.filter(i => (i?.stockQuantity || 0) <= (i?.minStockLevel || 10));

  const getStockColor = (current, threshold) => {
    if (current === 0) return 'text-red-600';
    if (current <= threshold) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStockBg = (current, threshold) => {
    if (current === 0) return 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
    if (current <= threshold) return 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800';
    return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="h-7 w-7 text-green-600" />
              Réapprovisionnement — Barriques & Cartons
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">Centre de contrôle unifié du réapprovisionnement automatisé</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button onClick={() => openModal()}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
              <Plus className="h-4 w-4" /> Nouvelle commande
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> {success}
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Alertes Barriques</p>
                </div>
                <p className={`text-2xl font-bold ${lowBarrels?.length > 0 ? 'text-red-500' : 'text-green-600'}`}>{lowBarrels?.length}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Alertes Cartons</p>
                </div>
                <p className={`text-2xl font-bold ${lowCartons?.length > 0 ? 'text-orange-500' : 'text-green-600'}`}>{lowCartons?.length}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <Wine className="h-4 w-4 text-amber-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Types barriques</p>
                </div>
                <p className="text-2xl font-bold text-amber-600">{barrelInventory?.length}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="h-4 w-4 text-blue-600" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Types cartons</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">{cartonInventory?.length}</p>
              </div>
            </div>

            {/* Alerts section */}
            {(lowBarrels?.length > 0 || lowCartons?.length > 0) && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Alertes de stock — Action requise
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lowBarrels?.map(item => {
                    const stock = item?.stockQuantity || item?.stock_quantity || 0;
                    const threshold = item?.reorderThreshold || item?.reorder_threshold || 20;
                    return (
                      <div key={item?.id} className={`rounded-xl p-4 border ${getStockBg(stock, threshold)}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Wine className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {BARREL_TYPE_LABELS?.[item?.barrelType || item?.barrel_type] || item?.barrelType}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Stock actuel</p>
                            <p className={`text-xl font-bold ${getStockColor(stock, threshold)}`}>{stock}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Seuil réappro</p>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{threshold}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => openModal({ productKey: `barrel__${item?.barrelType || item?.barrel_type}`, quantity: 50 })}
                          className="mt-3 w-full text-xs py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                        >
                          Commander
                        </button>
                      </div>
                    );
                  })}
                  {lowCartons?.map(item => {
                    const stock = item?.stockQuantity || 0;
                    const threshold = item?.minStockLevel || 10;
                    return (
                      <div key={item?.id} className={`rounded-xl p-4 border ${getStockBg(stock, threshold)}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item?.cartonType}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Stock actuel</p>
                            <p className={`text-xl font-bold ${getStockColor(stock, threshold)}`}>{stock}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Stock min</p>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{threshold}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => openModal({ productKey: `carton__${item?.id}`, quantity: 50 })}
                          className="mt-3 w-full text-xs py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                        >
                          Réapprovisionner
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Full stock overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Barrel stock */}
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                  <Wine className="h-4 w-4 text-amber-600" /> Niveaux de stock — Barriques
                </h2>
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Type</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Stock</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Seuil</th>
                        <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                      {barrelInventory?.map(item => {
                        const stock = item?.stockQuantity || item?.stock_quantity || 0;
                        const threshold = item?.reorderThreshold || item?.reorder_threshold || 20;
                        return (
                          <tr key={item?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">
                              {BARREL_TYPE_LABELS?.[item?.barrelType || item?.barrel_type] || item?.barrelType}
                            </td>
                            <td className={`px-4 py-3 text-right font-bold ${getStockColor(stock, threshold)}`}>{stock}</td>
                            <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{threshold}</td>
                            <td className="px-4 py-3 text-center">
                              {stock === 0 ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Rupture</span>
                              ) : stock <= threshold ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Bas</span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">OK</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Carton stock */}
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-blue-600" /> Niveaux de stock — Cartons
                </h2>
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                      <tr>
                        <th className="text-left px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Type</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Stock</th>
                        <th className="text-right px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Min</th>
                        <th className="text-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                      {cartonInventory?.map(item => {
                        const stock = item?.stockQuantity || 0;
                        const threshold = item?.minStockLevel || 10;
                        return (
                          <tr key={item?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">{item?.cartonType}</td>
                            <td className={`px-4 py-3 text-right font-bold ${getStockColor(stock, threshold)}`}>{stock}</td>
                            <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{threshold}</td>
                            <td className="px-4 py-3 text-center">
                              {stock === 0 ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Rupture</span>
                              ) : stock <= threshold ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Bas</span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">OK</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Enhanced Multi-Line Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nouvelle commande de réapprovisionnement</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Réf. {orderRef}</p>
              </div>
              <button onClick={() => setShowOrderModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body - scrollable */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Column headers */}
              <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 px-1">
                <div className="col-span-4">Produit</div>
                <div className="col-span-3">Fournisseur</div>
                <div className="col-span-2 text-center">Quantité</div>
                <div className="col-span-2 text-right">Prix unitaire (€)</div>
                <div className="col-span-1"></div>
              </div>

              {/* Order lines */}
              {orderLines?.map((line, idx) => {
                const suppliersForLine = getSuppliersForProduct(line?.productKey);
                return (
                  <div key={line?.id} className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
                    <div className="flex items-center justify-between mb-3 md:hidden">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Ligne {idx + 1}</span>
                      {orderLines?.length > 1 && (
                        <button onClick={() => removeLine(line?.id)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                      {/* Product */}
                      <div className="md:col-span-4">
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 md:hidden">Produit</label>
                        <select
                          value={line?.productKey}
                          onChange={e => updateLine(line?.id, 'productKey', e?.target?.value)}
                          className="w-full border border-gray-300 dark:border-slate-500 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="">-- Choisir un produit --</option>
                          <optgroup label="🍷 Barriques">
                            {BARREL_OPTIONS?.map(opt => (
                              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                            ))}
                          </optgroup>
                          {cartonInventory?.length > 0 && (
                            <optgroup label="📦 Cartons">
                              {cartonInventory?.map(ci => (
                                <option key={ci?.id} value={`carton__${ci?.id}`}>{ci?.cartonType}</option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>

                      {/* Supplier */}
                      <div className="md:col-span-3">
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 md:hidden">Fournisseur</label>
                        <select
                          value={line?.supplierId}
                          onChange={e => updateLine(line?.id, 'supplierId', e?.target?.value)}
                          disabled={!line?.productKey}
                          className="w-full border border-gray-300 dark:border-slate-500 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">-- Fournisseur --</option>
                          {suppliersForLine?.map(s => (
                            <option key={s?.id} value={s?.id}>
                              {s?.name}{(s?.unitPrice || s?.unit_price) ? ` (${parseFloat(s?.unitPrice || s?.unit_price || 0)?.toFixed(2)} €/u)` : ''}
                            </option>
                          ))}
                        </select>
                        {line?.supplierId && suppliersForLine?.length === 0 && (
                          <p className="text-xs text-orange-500 mt-1">Aucun fournisseur disponible</p>
                        )}
                      </div>

                      {/* Quantity */}
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 md:hidden">Quantité</label>
                        <input
                          type="number"
                          min="1"
                          value={line?.quantity}
                          onChange={e => updateLine(line?.id, 'quantity', e?.target?.value)}
                          className="w-full border border-gray-300 dark:border-slate-500 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent text-center"
                        />
                      </div>

                      {/* Unit price + line total */}
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 md:hidden">Prix unitaire (€)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line?.unitPrice}
                          onChange={e => updateLine(line?.id, 'unitPrice', e?.target?.value)}
                          className="w-full border border-gray-300 dark:border-slate-500 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent text-right"
                        />
                        {line?.lineTotal > 0 && (
                          <p className="text-xs text-green-600 dark:text-green-400 font-semibold text-right mt-1">
                            = {line?.lineTotal?.toFixed(2)} €
                          </p>
                        )}
                      </div>

                      {/* Remove button (desktop) */}
                      <div className="hidden md:flex md:col-span-1 items-start justify-center pt-2">
                        {orderLines?.length > 1 && (
                          <button onClick={() => removeLine(line?.id)} className="text-red-400 hover:text-red-600 p-1 rounded">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add line button */}
              <button
                onClick={addLine}
                className="w-full py-2.5 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:border-green-400 hover:text-green-600 dark:hover:border-green-500 dark:hover:text-green-400 flex items-center justify-center gap-2 transition-colors"
              >
                <Plus className="h-4 w-4" /> Ajouter une ligne
              </button>

              {/* Order total */}
              {totalOrderAmount > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total commande</span>
                  <span className="text-xl font-bold text-green-600 dark:text-green-400">{totalOrderAmount?.toFixed(2)} €</span>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes / Instructions</label>
                <textarea
                  value={orderNotes}
                  onChange={e => setOrderNotes(e?.target?.value)}
                  rows={2}
                  placeholder="Instructions particulières, délai souhaité..."
                  className="w-full border border-gray-300 dark:border-slate-500 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-gray-200 dark:border-slate-700 flex flex-col sm:flex-row justify-between gap-3 flex-shrink-0">
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || saving}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {sendingEmail ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {sendingEmail ? 'Envoi en cours...' : 'Envoyer par email au fournisseur'}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={saving || sendingEmail}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  {saving ? 'Enregistrement...' : 'Valider la commande'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
